'use client';

/**
 * AgentFlowWidget - Glass box visualization of agent reasoning
 *
 * Shows the step-by-step flow of agent simulation:
 * Entity -> Context -> Rules -> Prompt -> Reasoning -> Decision
 *
 * Each step is expandable to show full details.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain agents
 */

import { useState, useEffect } from 'react';
import {
  Bot,
  Database,
  FileText,
  MessageSquare,
  Brain,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Loader2,
  AlertCircle,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useSimulationStreamStore, type SimulationStep as StreamStep } from '@/lib/stores/simulation-stream-store';
import { api } from '@/lib/api/client';

// OODA-L message/step format from /ki/history endpoint
// Uses same pattern as agents for audit trail
interface MessageDetail {
  cdk: string | null;
  turn_number: number;
  body: string;
  subject: string | null;  // Phase name (OBSERVE, ORIENT, DECIDE)
  strategy: string | null;  // Capability name
  timestamp: string | null;
  author_name: string;
  // Structured audit fields (same as agents)
  step_entity: string | null;   // Entity data (OBSERVE)
  step_context: string | null;  // Enriched context (ORIENT)
  step_rules: string | null;    // Rules found (ORIENT)
  step_prompt: string | null;   // LLM synthesis (DECIDE)
}

interface ApprovalInfo {
  status: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejected_by: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
}

// /ki/history response format (OODA-L conversations)
interface SimulationResponse {
  simulation_id: string;
  entity_sid: string | null;
  entity_type: string | null;
  entity_name: string | null;
  project_sid: string | null;
  project_name: string | null;
  status: string | null;
  error: string | null;
  created_at: string | null;
  completed_at: string | null;
  turn_count: number | null;
  messages: MessageDetail[];
  approval: ApprovalInfo | null;
}

// OODA phase icons
const PHASE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  OBSERVE: Database,
  ORIENT: FileText,
  DECIDE: Brain,
  ACT: Zap,
  LEARN: CheckCircle,
};

// OODA phase colors
const PHASE_COLORS: Record<string, string> = {
  OBSERVE: 'bg-blue-100 text-blue-800 border-blue-200',
  ORIENT: 'bg-purple-100 text-purple-800 border-purple-200',
  DECIDE: 'bg-orange-100 text-orange-800 border-orange-200',
  ACT: 'bg-green-100 text-green-800 border-green-200',
  LEARN: 'bg-gray-100 text-gray-800 border-gray-200',
};

// Legacy step icons (for old format compatibility)
const STEP_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  entity: Database,
  context: FileText,
  rules: FileText,
  prompt: MessageSquare,
  reasoning: Brain,
  decision: CheckCircle,
};

const STEP_COLORS: Record<string, string> = {
  entity: 'bg-blue-100 text-blue-800 border-blue-200',
  context: 'bg-purple-100 text-purple-800 border-purple-200',
  rules: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  prompt: 'bg-gray-100 text-gray-800 border-gray-200',
  reasoning: 'bg-orange-100 text-orange-800 border-orange-200',
  decision: 'bg-green-100 text-green-800 border-green-200',
};

export function AgentFlowWidget() {
  const [simulation, setSimulation] = useState<SimulationResponse | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedNode = useGraphSelectionStore((s) => s.selectedNode);
  const projectId = useGraphSelectionStore((s) => s.projectId);
  const setProjectContext = useGraphSelectionStore((s) => s.setProjectContext);

  // SSE streaming state from store
  const streamState = useSimulationStreamStore();
  const isStreaming = selectedNode?.type === 'simulation-streaming';

  // Fetch simulation when a completed simulation is selected
  useEffect(() => {
    if (selectedNode?.type === 'simulation' && selectedNode.id) {
      fetchSimulation(selectedNode.id);
    } else if (!isStreaming) {
      setSimulation(null);
    }
  }, [selectedNode?.id, selectedNode?.type, isStreaming]);

  const fetchSimulation = async (simId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await api.getStrategyData<SimulationResponse>('ki_simulation_detail', { simulation_id: simId });
      if (!res.success) throw new Error(res.error || 'ki_simulation_detail failed');
      setSimulation(res.data);

      // Update project context for canvas graph - only if not already set
      // (SimulationsList sets this with better data from /ki/ask response)
      if (res.data.project_sid && !projectId) {
        setProjectContext(res.data.project_sid, {
          name: res.data.project_name ?? '',
          rag_status: 'unknown', // Will be computed by ObeyaWidget
          rag_label: '',
          rfq_count: 0,
          po_count: 0,
        });
      }
    } catch (err) {
      setError('Failed to load simulation');
      console.error('Failed to fetch simulation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleStep = (stepNumber: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepNumber)) {
        next.delete(stepNumber);
      } else {
        next.add(stepNumber);
      }
      return next;
    });
  };

  // ==========================================================================
  // STREAMING MODE - Show live SSE steps
  // ==========================================================================
  if (isStreaming) {
    return (
      <ScrollArea className="h-full">
        <div className="p-4 space-y-4">
          {/* Streaming Header */}
          <Card className="border-blue-200 bg-blue-50/50">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-500 animate-pulse" />
                  {streamState.entityName ?? streamState.entitySid ?? 'Running Analysis'}
                </CardTitle>
                <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
                  {streamState.entityType ?? 'entity'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Streaming...
                </span>
                <span>Steps: {streamState.steps.length}</span>
              </div>
            </CardContent>
          </Card>

          {/* Live Steps */}
          <div className="space-y-2">
            {streamState.steps.map((step, index) => {
              const Icon = STEP_ICONS[step.step_type] ?? FileText;
              const colorClass = STEP_COLORS[step.step_type] ?? 'bg-gray-100 text-gray-800';

              return (
                <div key={step.step_number}>
                  {index > 0 && (
                    <div className="flex justify-center py-1">
                      <div className="w-0.5 h-4 bg-border" />
                    </div>
                  )}
                  <Card
                    className={cn(
                      'cursor-pointer transition-all animate-in fade-in slide-in-from-left-2 duration-300',
                      'hover:shadow-md'
                    )}
                    onClick={() => toggleStep(step.step_number)}
                  >
                    <CardHeader className="p-3">
                      <div className="flex items-center gap-3">
                        <div className={cn('p-2 rounded-full', colorClass)}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{step.label}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {step.step_type}
                            </Badge>
                          </div>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </div>
                    </CardHeader>
                    {expandedSteps.has(step.step_number) && (
                      <CardContent className="pt-0 pb-3">
                        <pre className="text-xs bg-muted p-2 rounded-md overflow-auto max-h-60">
                          {JSON.stringify(step.data, null, 2)}
                        </pre>
                      </CardContent>
                    )}
                  </Card>
                </div>
              );
            })}

            {/* Pending indicator */}
            {streamState.status === 'streaming' && (
              <div className="flex justify-center py-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Waiting for next step...</span>
                </div>
              </div>
            )}
          </div>

          {/* Error state */}
          {streamState.status === 'error' && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5" />
                <span>{streamState.error}</span>
              </CardContent>
            </Card>
          )}

          {/* Complete state - show judgment */}
          {streamState.status === 'complete' && streamState.finalJudgment && (
            <Card className="border-2 border-primary/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  Recommendation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground">
                    {streamState.finalJudgment.action}
                  </Badge>
                  <Badge variant="outline">
                    {streamState.finalJudgment.action_type}
                  </Badge>
                  <Badge
                    className={cn(
                      'ml-auto',
                      streamState.finalJudgment.confidence === 'high'
                        ? 'bg-green-100 text-green-800'
                        : streamState.finalJudgment.confidence === 'medium'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-gray-100 text-gray-800'
                    )}
                  >
                    {streamState.finalJudgment.confidence}
                  </Badge>
                </div>
                {streamState.totalMs && (
                  <p className="text-xs text-muted-foreground">
                    Total time: {(streamState.totalMs / 1000).toFixed(1)}s
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>
    );
  }

  // ==========================================================================
  // STATIC MODE - Show completed simulation from API
  // ==========================================================================

  // Empty state
  if (!selectedNode || selectedNode.type !== 'simulation') {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Bot className="w-16 h-16 mb-4 opacity-50" />
        <p className="text-lg">Agent Flow</p>
        <p className="text-sm">Select a simulation to view its reasoning flow</p>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin mb-4" />
        <p className="text-sm">Loading simulation...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <AlertCircle className="w-8 h-8 mb-4 text-red-500" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!simulation) {
    return null;
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Header */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Bot className="w-5 h-5" />
                {simulation.entity_name ?? simulation.entity_sid ?? 'Analysis'}
              </CardTitle>
              <Badge variant="outline">{simulation.entity_type ?? 'unknown'}</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="truncate max-w-[200px]">ID: {simulation.simulation_id}</span>
              {simulation.project_name && (
                <span>Project: {simulation.project_name}</span>
              )}
              <Badge
                className={cn(
                  simulation.status === 'complete'
                    ? 'bg-green-100 text-green-800'
                    : simulation.status === 'error'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                )}
              >
                {simulation.status ?? 'pending'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Error display */}
        {simulation.error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{simulation.error}</span>
            </CardContent>
          </Card>
        )}

        {/* OODA-L Steps (audit trail) */}
        {simulation.messages && simulation.messages.length > 0 ? (
          <div className="space-y-2">
            {simulation.messages.map((msg, index) => {
              // Get phase from subject (OBSERVE, ORIENT, DECIDE, etc.)
              const phase = msg.subject ?? 'UNKNOWN';
              const PhaseIcon = PHASE_ICONS[phase] ?? FileText;
              const phaseColor = PHASE_COLORS[phase] ?? 'bg-gray-100 text-gray-800';

              // Get the appropriate step data based on phase (structured fields)
              const getStepData = () => {
                if (msg.step_entity) return { type: 'Entity', data: msg.step_entity };
                if (msg.step_context) return { type: 'Context', data: msg.step_context };
                if (msg.step_rules) return { type: 'Rules', data: msg.step_rules };
                if (msg.step_prompt) return { type: 'Synthesis', data: msg.step_prompt };
                return null;
              };
              const stepInfo = getStepData();

              // Try to parse JSON for display
              const parseStepData = (data: string | null) => {
                if (!data) return null;
                try {
                  return JSON.parse(data);
                } catch {
                  return data;
                }
              };

              return (
                <div key={msg.cdk ?? index}>
                  {/* Connection line */}
                  {index > 0 && (
                    <div className="flex justify-center py-1">
                      <div className="w-0.5 h-4 bg-border" />
                    </div>
                  )}

                  {/* Step card */}
                  <Card
                    className="cursor-pointer transition-colors hover:shadow-md"
                    onClick={() => toggleStep(msg.turn_number)}
                  >
                    <CardHeader className="p-3">
                      <div className="flex items-center gap-3">
                        <div className={cn('p-2 rounded-full', phaseColor)}>
                          <PhaseIcon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{msg.strategy ?? 'step'}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {phase}
                            </Badge>
                            {stepInfo && (
                              <Badge variant="secondary" className="text-[10px]">
                                {stepInfo.type}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {expandedSteps.has(msg.turn_number) ? (
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>

                    {/* Expanded content - show structured step data */}
                    {expandedSteps.has(msg.turn_number) && (
                      <CardContent className="pt-0 pb-3 space-y-2">
                        {stepInfo ? (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {stepInfo.type} Data
                            </p>
                            <pre className="text-xs bg-muted p-2 rounded-md overflow-auto max-h-60">
                              {typeof parseStepData(stepInfo.data) === 'object'
                                ? JSON.stringify(parseStepData(stepInfo.data), null, 2)
                                : stepInfo.data}
                            </pre>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">No data recorded</p>
                        )}
                      </CardContent>
                    )}
                  </Card>
                </div>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-4 text-center text-muted-foreground">
              <Brain className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">OODA-L analysis complete</p>
              <p className="text-xs">No step details recorded</p>
            </CardContent>
          </Card>
        )}

        {/* Approval Status */}
        {simulation.approval && simulation.approval.status && (
          <Card className={cn(
            'border-2',
            simulation.approval.status === 'approved'
              ? 'border-green-200 bg-green-50/50'
              : simulation.approval.status === 'rejected'
              ? 'border-red-200 bg-red-50/50'
              : 'border-yellow-200 bg-yellow-50/50'
          )}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                {simulation.approval.status === 'approved' ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : simulation.approval.status === 'rejected' ? (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                ) : (
                  <Loader2 className="w-5 h-5 text-yellow-500" />
                )}
                {simulation.approval.status === 'approved' ? 'Approved' :
                 simulation.approval.status === 'rejected' ? 'Rejected' : 'Pending Approval'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {simulation.approval.approved_by && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Approved by:</span> {simulation.approval.approved_by}
                </p>
              )}
              {simulation.approval.rejected_by && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Rejected by:</span> {simulation.approval.rejected_by}
                </p>
              )}
              {simulation.approval.rejection_reason && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Reason</p>
                  <p className="text-sm">{simulation.approval.rejection_reason}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </ScrollArea>
  );
}
